import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Pin, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export function BankBookingRulesDialog({ companyId }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acceptingKey, setAcceptingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        apiClient.get(`/companies/${companyId}/bank/booking-rules`),
        apiClient.get(`/companies/${companyId}/bank/booking-rule-suggestions`),
      ]);
      setRules(r.data || []);
      setSuggestions(s.data || []);
    } catch (e) {
      toast.error("Regeln konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => { setOpen(true); load(); };

  const remove = async (rule) => {
    try {
      await apiClient.delete(
        `/companies/${companyId}/bank/booking-rules/${encodeURIComponent(rule.vendor_key)}`);
      toast.success("Regel entfernt — künftige Zahlungen werden wieder normal vorgeschlagen");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    }
  };

  const accept = async (s) => {
    setAcceptingKey(s.vendor_key);
    try {
      await apiClient.post(`/companies/${companyId}/bank/booking-rules`, {
        vendor_name: s.vendor_name || s.vendor_key,
        account_id: s.account_id,
      });
      toast.success(`📌 Regel gespeichert: «${s.vendor_name || s.vendor_key}» → Konto ${s.account_code}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernehmen fehlgeschlagen");
    } finally {
      setAcceptingKey(null);
    }
  };

  const toggleAuto = async (r, enabled) => {
    try {
      await apiClient.patch(
        `/companies/${companyId}/bank/booking-rules/${encodeURIComponent(r.vendor_key)}`,
        { auto_book: enabled });
      setRules((rs) => rs.map((x) => (x.vendor_key === r.vendor_key ? { ...x, rule_auto_book: enabled } : x)));
      toast.success(enabled
        ? "Vollautomatik aktiviert — Belege dieser Gegenpartei werden künftig ohne Rückfrage verbucht"
        : "Vollautomatik deaktiviert — Konto wird nur noch vorgeschlagen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Änderung fehlgeschlagen");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5"
        title="Feste Buchungsregeln (Gegenpartei → Konto) ansehen und entfernen"
        data-testid="bank-booking-rules-button"
      >
        <Pin className="h-3 w-3" /> Buchungsregeln
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bank-booking-rules-dialog">
          <DialogHeader>
            <DialogTitle>Feste Buchungsregeln</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Zahlungen und Belege dieser Gegenparteien werden künftig automatisch mit dem hinterlegten
            Konto vorgeschlagen — du bestätigst mit einem Klick. Mit «Auto» wird ohne Rückfrage
            vollautomatisch verbucht. Alles jederzeit änderbar.
          </p>
          {loading ? (
            <div className="py-6 text-center text-xs text-slate-400">Lade …</div>
          ) : (
            <>
              {rules.length === 0 ? (
                <div className="py-4 text-center text-xs text-slate-400" data-testid="bank-booking-rules-empty">
                  Noch keine Regeln. Setze beim Verbuchen den Haken «Immer so buchen» — oder übernimm unten einen Vorschlag.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {rules.map((r) => (
                    <div
                      key={r.vendor_key}
                      className="flex items-center gap-2 py-2 text-sm"
                      data-testid={`booking-rule-${r.vendor_key}`}
                    >
                      <Pin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      <span className="flex-1 truncate text-slate-800">{r.vendor_name || r.vendor_key}</span>
                      <span className="font-mono text-xs text-slate-600">{r.account_code}</span>
                      <span className="text-xs text-slate-500 truncate max-w-[8rem]">{r.account_name}</span>
                      <label
                        className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer flex-shrink-0"
                        title="Vollautomatik: Belege dieser Gegenpartei werden ohne Rückfrage verbucht (Bestätigung durch Admin/Buchhalter). Ohne Haken: Konto wird nur vorgeschlagen."
                      >
                        <input
                          type="checkbox"
                          checked={!!r.rule_auto_book}
                          onChange={(e) => toggleAuto(r, e.target.checked)}
                          className="accent-emerald-700"
                          data-testid={`booking-rule-auto-${r.vendor_key}`}
                        />
                        Auto
                      </label>
                      <button
                        type="button"
                        onClick={() => remove(r)}
                        className="text-slate-400 hover:text-rose-600 transition-colors"
                        title="Regel entfernen"
                        data-testid={`booking-rule-delete-${r.vendor_key}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {suggestions.length > 0 && (
                <div className="mt-3" data-testid="bank-rule-suggestions">
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-emerald-600" />
                    Vorschläge aus deiner Historie
                  </div>
                  <p className="text-[11px] text-slate-400 mb-1">
                    Diese Gegenparteien hast du mehrfach gleich verbucht — per Klick als feste Regel übernehmen.
                  </p>
                  <div className="divide-y divide-slate-100">
                    {suggestions.map((s) => (
                      <div
                        key={s.vendor_key}
                        className="flex items-center gap-2 py-2 text-sm"
                        data-testid={`rule-suggestion-${s.vendor_key}`}
                      >
                        <span className="flex-1 truncate text-slate-800">{s.vendor_name || s.vendor_key}</span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0" title="So oft wurde diese Zuordnung gebucht">
                          {s.count}× gebucht
                        </span>
                        <span className="font-mono text-xs text-slate-600">{s.account_code}</span>
                        <span className="text-xs text-slate-500 truncate max-w-[8rem]">{s.account_name}</span>
                        <button
                          type="button"
                          onClick={() => accept(s)}
                          disabled={acceptingKey === s.vendor_key}
                          className="flex-shrink-0 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 transition-colors flex items-center gap-1 disabled:opacity-50"
                          title="Als feste Regel übernehmen — künftige Zahlungen dieser Gegenpartei werden mit diesem Konto vorgeschlagen"
                          data-testid={`rule-suggestion-accept-${s.vendor_key}`}
                        >
                          <Pin className="h-3 w-3" />
                          {acceptingKey === s.vendor_key ? "Speichere…" : "Übernehmen"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
